import { rejectReasonLabel, stateLabelOf } from './derive';

/** 태스크 상세 — 버전 기록 · 다음 행동 문구 · Activity 기록 */

export const versionsOf = (task) =>
  [
    ...(task.history ?? []).map((v) => ({
      v: v.version,
      deliverable: v.deliverable,
      review: v.review,
    })),
    ...(task.deliverable
      ? [
          {
            v: task.version ?? (task.history?.length ?? 0) + 1,
            deliverable: task.deliverable,
            review: task.review,
          },
        ]
      : []),
  ].reverse();

export const approvedVersionOf = (task) =>
  task.state === 'approved' && task.deliverable
    ? {
        v: task.version ?? (task.history?.length ?? 0) + 1,
        deliverable: task.deliverable,
        review: task.review,
      }
    : null;

export const slotLabelsOf = (d) => (d?.slots ?? []).filter((s) => s.url).map((s) => s.l);

const reasonText = (key) => (key ? (rejectReasonLabel(key) ?? key) : '');

export const markedText = (failed, slotLabels) =>
  failed.map((l) => (slotLabels.includes(l) ? `Link slot · ${l}` : l)).join(', ');

export const reviewRows = (version, items) => {
  const r = version.review;
  const slots = slotLabelsOf(version.deliverable);
  if (!r || r.returned || !['approved', 'rejected'].includes(r.result)) return null;
  const mark = (label) =>
    r.result === 'approved'
      ? 'pass'
      : (r.failedItems ?? []).includes(label)
        ? 'fail'
        : !r.passedItems || r.passedItems.includes(label)
          ? 'pass'
          : null;
  const list = version.deliverable?.checked?.length ? version.deliverable.checked : items;
  return {
    slots: slots.map((label) => ({ label, result: mark(label) })),
    items: list.map((label) => ({ label, result: mark(label) })),
  };
};

const N = (tone, title, text = '') => ({ tone, title, text });

export const EYEBROW = {
  act: 'Your next step',
  warn: 'Your next step',
  done: 'Done',
  ro: 'Status',
};

export const nextActionOf = (c) => {
  const { task, isA, isR, leader, ended, preLock, lockTip, alert, gate, next, name, fmt } = c;
  const versions = versionsOf(task);
  const lv = versions[0]?.v ?? 1;
  const nv = versions.length ? lv + 1 : 1;
  const asg = name(task.assigneeId);
  const rcv = name(task.receiverId);
  const exp = fmt(c.expected);
  const state = stateLabelOf(task);
  const last = [...c.comments]
    .reverse()
    .find((x) => !x.parentId && (x.type === 'rejection' || x.type === 'return'));
  const lastReason = last ? reasonText(last.reason) || 'changes' : '';
  const titles = (list) => list.map((x) => `“${x.title}”`).join(', ');
  let r;
  if (ended) r = N('ro', 'Read-only', 'This project is closed for changes. You can still read everything here.');
  else if (preLock) r = N('wait', lockTip, 'You can read this task now. Actions open when your team starts.');
  else if (task.state === 'cancelled') r = N('ro', 'Cancelled', 'This task is no longer part of the relay.');
  else if (isA) {
    r = {
      assigned: () =>
        N('act', 'Accept or decline this task', `Set your expected submit time when you accept. ${rcv} reviews it after you submit.`),
      in_progress: () =>
        gate.length
          ? N('wait', 'Keep working · submit opens later', `Submit opens when ${titles(gate)} ${gate.length > 1 ? 'are' : 'is'} approved.`)
          : task.isBlocked
            ? N('warn', 'Blocker open · you can still submit', `Submit when the work is ready, and resolve the blocker once it’s sorted. ${rcv} reviews it · expected ${exp}.`)
            : N('act', task.isFinal ? 'Submit the final deliverable' : 'Finish the work, then submit', `Check every Task List item first. ${rcv} reviews it · expected ${exp}.`),
      rework_requested: () =>
        N('warn', `Fix and submit v${nv}`, last
          ? `${name(last.authorId)} ${last.type === 'return' ? 'returned' : 'rejected'} v${lv}: ${lastReason}.`
          : `Submit v${nv} to ${rcv}.`),
      submitted: () =>
        N('wait', `Waiting for ${rcv} to review v${lv}`, `Submitted ${fmt(versions[0]?.deliverable?.submittedAt)}. You get a notification when it’s reviewed.`),
      approved: () =>
        N('done', `Approved by ${rcv}`, next.length ? `Next in the relay: ${titles(next)}.` : 'Nothing else to do on this task.'),
      declined: () => N('ro', 'You declined this task', 'The leader can reassign it.'),
    }[task.state]?.() ?? N('ro', state);
  } else if (isR) {
    r = {
      submitted: () =>
        N('act', `Review v${lv} now`, 'Mark every link slot and Task List item ✓ or ✗, then approve or reject.'),
      assigned: () =>
        N('wait', `Waiting for ${asg} to accept`, `Assigned ${fmt(task.assignedAt)} · due ${fmt(c.due)}.`),
      in_progress: () =>
        N('wait', `Waiting for ${asg} to submit`, `Expected ${exp}. You get a notification when it arrives.`),
      rework_requested: () =>
        N('wait', `Waiting for ${asg} to submit v${nv}`, `v${lv} went back with: ${lastReason || 'changes'}. Expected ${exp}.`),
      approved: () =>
        N('done', `You approved v${lv}`, 'If something turns out missing, ask a question first, then return it to the sender.'),
      declined: () => N('ro', `${asg} declined this task`, 'The leader reassigns it.'),
    }[task.state]?.() ?? N('ro', state);
  } else if (leader && task.state === 'declined') {
    r = N('act', 'Reassign this task', `${asg} declined${task.declineReason ? `: ${task.declineReason}` : '.'}`);
  } else if (leader && task.state !== 'approved') {
    r = N('wait', `${state} · ${asg} → ${rcv}`, 'You can reassign or change the Task List if plans change.');
  } else {
    r = N('ro', `${state} · view only`, `${asg} → ${rcv}. You can read and comment.`);
  }
  if (alert && !ended) r = { tone: alert[0] === 'warn' ? 'warn' : r.tone, title: alert[1], text: alert[2] };
  return { ...r, eyebrow: r.tone === 'wait' ? (isA || isR ? 'Waiting' : 'Status') : EYEBROW[r.tone] };
};

export const KEY_EVENTS = [
  'assigned',
  'reassigned',
  'accepted',
  'declined',
  'receiver',
  'submitted',
  'approved',
  'rejected',
  'returned',
];

const REASON_TEXT = { member_removed: 'Member left the team', moved_earlier: 'Moved earlier' };

export const activityOf = (c) => {
  const { task, comments, name, leaderName, viewerId, seeExpected, fmt } = c;
  const out = [];
  let n = 0;
  const add = (k, at, who, title, detail = '', extra = {}) =>
    out.push({ k, at, who, title, detail, n: (n += 1), ...extra });
  const byName = (by) => (by === 'leader' ? leaderName : by === 'ops' || !by ? 'Ops team' : name(by));
  const log = task.activity ?? [];
  const first = log.find((e) => e.type === 'reassign');
  const firstReceiver = log.find((e) => e.type === 'receiver');
  const assigned = log.find((e) => e.type === 'assigned');
  add(
    'assigned',
    assigned?.at ?? task.assignedAt,
    leaderName,
    `Assigned to ${name(assigned?.to ?? first?.from ?? task.assigneeId)}`,
    `Receiver ${name(firstReceiver?.from ?? task.receiverId)}`,
  );
  if (task.acceptedAt) add('accepted', task.acceptedAt, name(task.acceptedById ?? task.assigneeId), 'Accepted');
  if (task.state === 'declined' && !log.some((e) => e.type === 'decline')) {
    add('declined', task.assignedAt, name(task.assigneeId), 'Declined', task.declineReason ?? '');
  }
  log.forEach((e) => {
    const reason = REASON_TEXT[e.reason] ?? e.reason ?? '';
    if (e.type === 'reassign') {
      add('reassigned', e.at, byName(e.by), 'Reassigned', [`${name(e.from)} → ${name(e.to)}`, reason].filter(Boolean).join(' · '));
    } else if (e.type === 'receiver') {
      add('receiver', e.at, byName(e.by), 'Receiver changed', [`${name(e.from)} → ${name(e.to)}`, reason].filter(Boolean).join(' · '));
    } else if (e.type === 'scope') {
      add('scope', e.at, byName(e.by), 'Task List changed', reason);
    } else if (e.type === 'decline') {
      add('declined', e.at, name(e.by), 'Declined', reason);
    } else if (e.type === 'blocker') {
      add('resolved', e.at, name(e.by), 'Resolved the blocker', 'Solved it myself');
    } else if (e.type === 'expected' && (seeExpected || e.by === viewerId)) {
      add('expected', e.at, name(e.by), 'Changed expected submit time', e.from ? `${fmt(e.from)} → ${fmt(e.to)}` : fmt(e.to), { pmOnly: true });
    }
  });
  [...versionsOf(task)].reverse().forEach((v) => {
    const d = v.deliverable ?? {};
    add('submitted', d.submittedAt, name(d.submittedBy ?? task.assigneeId), `Submitted v${v.v}`, d.summary ?? '');
    if (v.review?.result === 'approved') add('approved', v.review.at, name(task.receiverId), `Approved v${v.v}`);
  });
  comments
    .filter((cm) => !cm.parentId)
    .forEach((cm) => {
      const who = name(cm.authorId);
      if (cm.type === 'rejection') {
        add('rejected', cm.createdAt, who, 'Rejected', [cm.detail?.replace(/^Marked ✗:\s*/, '✗ '), reasonText(cm.reason)].filter(Boolean).join(' · '));
      } else if (cm.type === 'return') {
        add('returned', cm.createdAt, who, 'Returned to sender', reasonText(cm.reason));
      } else if (cm.type === 'blocker') {
        add('blocker', cm.createdAt, who, 'Raised a blocker', cm.body);
      } else if (cm.type === 'question') {
        add('comment', cm.createdAt, who, `Asked ${name(cm.targetUserId)}`, cm.body);
      } else {
        add('comment', cm.createdAt, who, 'Commented', cm.body);
      }
    });
  (task.contributors ?? []).forEach((x) => {
    if (x.reason === 'Blocker resolved') {
      add('resolved', x.at, name(x.addedBy), 'Resolved the blocker', `Helped by ${name(x.userId)}`);
    } else {
      add('contributor', x.at, name(x.addedBy), 'Added a contributor', `${name(x.userId)} · ${x.reason}`);
    }
  });
  const time = (e) => (e.at ? new Date(e.at).getTime() : -Infinity);
  return out
    .sort((a, b) => time(b) - time(a) || b.n - a.n)
    .map((e) => ({ ...e, key: KEY_EVENTS.includes(e.k), time: e.at ? fmt(e.at) : '' }));
};
